import React, { useState, useEffect, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import LoadingScreen from './components/LoadingScreen';
import Navigation from './components/Navigation';
import ErrorBoundary from './components/ErrorBoundary';
import Hero from './components/Hero';
import About from './components/About';
import Features from './components/Features';
import Demo from './components/Demo';
import Pricing from './components/Pricing';
import FAQ from './components/FAQ';
import Contact from './components/Contact';
import '../styles/landing-3d.css';

const sections = [
  { id: 'hero', name: 'Home' },
  { id: 'about', name: 'About' },
  { id: 'features', name: 'Features' },
  { id: 'demo', name: 'Demo' },
  { id: 'pricing', name: 'Pricing' },
  { id: 'faq', name: 'FAQ' },
  { id: 'contact', name: 'Contact' },
];

// Debounce helper
function debounce(fn, delay) {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

function LandingPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [currentSection, setCurrentSection] = useState(0);

  // Cache element references
  const sectionElementsRef = useRef({});

  useEffect(() => {
    // 2 second loading screen
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  // Cache section elements after loading completes
  useEffect(() => {
    if (!isLoading) {
      sections.forEach(section => {
        sectionElementsRef.current[section.id] = document.getElementById(section.id);
      });
    }
  }, [isLoading]);

  useEffect(() => {
    // Track current section on scroll with debounce for performance
    const handleScroll = debounce(() => {
      const scrollPosition = window.scrollY + window.innerHeight / 2;
      const elements = sectionElementsRef.current;

      sections.forEach((section, index) => {
        const element = elements[section.id];
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setCurrentSection(prev => prev !== index ? index : prev);
          }
        }
      });
    }, 100);

    // Use passive listener for better scroll performance
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (index) => {
    const element = document.getElementById(sections[index].id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <AnimatePresence>
        {isLoading && <LoadingScreen />}
      </AnimatePresence>

      {!isLoading && (
        <>
          <Navigation
            sections={sections}
            currentSection={currentSection}
            onSectionClick={scrollToSection}
          />

          <main>
            {/* Wrap 3D components in Error Boundary for graceful crash handling */}
            <ErrorBoundary message="Failed to load 3D scene. Please refresh the page.">
              <Hero />
            </ErrorBoundary>
            <ErrorBoundary message="Failed to load 3D scene. Please refresh the page.">
              <About />
            </ErrorBoundary>
            <Features />
            <Demo />
            <Pricing />
            <FAQ />
            <Contact />
          </main>
        </>
      )}
    </>
  );
}

export default LandingPage;
